import { useState, useRef, useMemo, type KeyboardEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { Eye, EyeOff, Trash2, CheckCircle2, Zap, Copy, Check, Plus, X, Star } from 'lucide-react'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  useTinyStatus,
  useSaveTinyConfig,
  useTestTinyConnection,
  useRemoveTiny,
  useTinyShippingMethods,
} from '@/hooks/use-tiny'
import { SectionCard } from '@/components/shared/section-card'
import { FormSection } from '@/components/shared/form-section'
import { ContextualSaveBar } from '@/components/shared/contextual-save-bar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { API_BASE_URL } from '@/lib/api'

export const Route = createFileRoute('/_app/settings/integrations/tiny')({
  component: TinySettingsPage,
})

const ORDER_STATUSES = [
  { value: 'aberto', label: 'Em aberto' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'preparando_envio', label: 'Preparando envio' },
  { value: 'faturado', label: 'Faturado' },
]

const PRICE_STRATEGIES = [
  { value: 'zero', label: 'Preço zero (bonificação)' },
  { value: 'retail_price', label: 'Preço de venda' },
  { value: 'percentage', label: 'Percentual do preço de venda' },
]

const FALLBACK_SHIPPING_METHODS = [
  { code: 'C', name: 'Correios' },
  { code: 'T', name: 'Transportadora' },
  { code: 'X', name: 'Customizada' },
  { code: 'J', name: 'Jadlog' },
  { code: 'S', name: 'Sem Frete' },
  { code: 'LOGGI', name: 'Loggi' },
]

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'credito', label: 'Crédito' },
  { value: 'debito', label: 'Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'deposito', label: 'Depósito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'crediario', label: 'Crediário' },
  { value: 'duplicata_mercantil', label: 'Duplicata Mercantil' },
  { value: 'multiplas', label: 'Múltiplas' },
]

function TinySettingsPage() {
  const { data: status, isLoading } = useTinyStatus()
  const saveTinyConfig = useSaveTinyConfig()
  const testConnection = useTestTinyConnection()
  const removeTiny = useRemoveTiny()
  const { data: shippingMethods } = useTinyShippingMethods(status?.enabled ?? false)

  const [apiToken, setApiToken] = useState('')
  const [showApiToken, setShowApiToken] = useState(false)

  // Config form state
  const [defaultStatus, setDefaultStatus] = useState(status?.defaultStatus || 'aberto')
  const [shippingMethod, setShippingMethod] = useState(status?.shippingMethod || '')
  const [carrierName, setCarrierName] = useState(status?.carrierName || '')
  const [itemPriceStrategy, setItemPriceStrategy] = useState(status?.itemPriceStrategy || 'zero')
  const [itemPricePercent, setItemPricePercent] = useState(
    status?.itemPricePercent?.toString() || ''
  )
  const [formaPagamento, setFormaPagamento] = useState(status?.formaPagamento || '')
  // orderTags stored as comma-separated string for API compatibility; chips UI uses parsed array
  const [orderTags, setOrderTags] = useState(status?.orderTags || '')
  const [orderTagInput, setOrderTagInput] = useState('')
  const [formaFrete, setFormaFrete] = useState(status?.formaFrete || '')
  // financeCategories: list of category names; financeCategory: the default one
  const [financeCategories, setFinanceCategories] = useState<string[]>(
    status?.financeCategories || []
  )
  const [financeCategory, setFinanceCategory] = useState(status?.financeCategory || '')
  const [financeCategoryInput, setFinanceCategoryInput] = useState('')
  const financeCategoryInputRef = useRef<HTMLInputElement>(null)
  const orderTagInputRef = useRef<HTMLInputElement>(null)

  // Sync state when status loads
  const [synced, setSynced] = useState(false)
  if (status && !synced) {
    setDefaultStatus(status.defaultStatus || 'aberto')
    setShippingMethod(status.shippingMethod || '')
    setCarrierName(status.carrierName || '')
    setItemPriceStrategy(status.itemPriceStrategy || 'zero')
    setItemPricePercent(status.itemPricePercent?.toString() || '')
    setOrderTags(status.orderTags || '')
    setFormaFrete(status.formaFrete || '')
    setFormaPagamento(status.formaPagamento || '')
    setFinanceCategories(status.financeCategories || [])
    setFinanceCategory(status.financeCategory || '')
    setSynced(true)
  }

  // Dirty state: compare current form values against server values
  const isDirty = useMemo(() => {
    if (!status?.enabled) return false
    return (
      defaultStatus !== (status.defaultStatus || 'aberto') ||
      shippingMethod !== (status.shippingMethod || '') ||
      carrierName !== (status.carrierName || '') ||
      itemPriceStrategy !== (status.itemPriceStrategy || 'zero') ||
      itemPricePercent !== (status.itemPricePercent?.toString() || '') ||
      orderTags !== (status.orderTags || '') ||
      formaFrete !== (status.formaFrete || '') ||
      formaPagamento !== (status.formaPagamento || '') ||
      JSON.stringify(financeCategories) !== JSON.stringify(status.financeCategories || []) ||
      financeCategory !== (status.financeCategory || '')
    )
  }, [
    status,
    defaultStatus,
    shippingMethod,
    carrierName,
    itemPriceStrategy,
    itemPricePercent,
    orderTags,
    formaFrete,
    formaPagamento,
    financeCategories,
    financeCategory,
  ])

  const handleDiscard = () => {
    if (!status) return
    setDefaultStatus(status.defaultStatus || 'aberto')
    setShippingMethod(status.shippingMethod || '')
    setCarrierName(status.carrierName || '')
    setItemPriceStrategy(status.itemPriceStrategy || 'zero')
    setItemPricePercent(status.itemPricePercent?.toString() || '')
    setOrderTags(status.orderTags || '')
    setFormaFrete(status.formaFrete || '')
    setFormaPagamento(status.formaPagamento || '')
    setFinanceCategories(status.financeCategories || [])
    setFinanceCategory(status.financeCategory || '')
  }

  if (isLoading) {
    return <PageSkeleton sections={3} />
  }

  const handleConnect = async () => {
    if (!apiToken.trim()) return
    await saveTinyConfig.mutateAsync({ apiToken })
    setApiToken('')
    setSynced(false)
  }

  const handleSaveConfig = async () => {
    await saveTinyConfig.mutateAsync({
      defaultStatus,
      shippingMethod: shippingMethod || undefined,
      carrierName: carrierName || undefined,
      itemPriceStrategy: itemPriceStrategy as 'zero' | 'retail_price' | 'percentage',
      itemPricePercent: itemPricePercent ? Number(itemPricePercent) : null,
      orderTags: orderTags || null,
      formaFrete: formaFrete || null,
      formaPagamento: formaPagamento || null,
      financeCategories,
      financeCategory: financeCategory || null,
    })
  }

  // --- Finance category helpers ---
  const handleAddFinanceCategory = () => {
    const trimmed = financeCategoryInput.trim()
    if (!trimmed || financeCategories.includes(trimmed)) return
    const newList = [...financeCategories, trimmed]
    setFinanceCategories(newList)
    // Auto-set as default when it's the first category
    if (newList.length === 1) setFinanceCategory(trimmed)
    setFinanceCategoryInput('')
    financeCategoryInputRef.current?.focus()
  }

  const handleFinanceCategoryKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddFinanceCategory()
    }
  }

  const handleRemoveFinanceCategory = (cat: string) => {
    const newList = financeCategories.filter((c) => c !== cat)
    setFinanceCategories(newList)
    // If removing the current default, reset to first remaining (or empty)
    if (financeCategory === cat) {
      setFinanceCategory(newList[0] ?? '')
    }
  }

  // --- Order tag helpers ---
  const orderTagsArray = orderTags
    ? orderTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : []

  const handleAddOrderTag = () => {
    const trimmed = orderTagInput.trim()
    if (!trimmed || orderTagsArray.includes(trimmed)) return
    const newTags = [...orderTagsArray, trimmed]
    setOrderTags(newTags.join(', '))
    setOrderTagInput('')
    orderTagInputRef.current?.focus()
  }

  const handleOrderTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddOrderTag()
    }
  }

  const handleRemoveOrderTag = (tag: string) => {
    const newTags = orderTagsArray.filter((t) => t !== tag)
    setOrderTags(newTags.join(', '))
  }

  const envioOptions = shippingMethods?.length
    ? shippingMethods.map((m) => ({ code: m.id.toString(), name: m.nome }))
    : FALLBACK_SHIPPING_METHODS

  return (
    <div className="space-y-6">
      <ContextualSaveBar
        isDirty={isDirty}
        isSaving={saveTinyConfig.isPending}
        onSave={handleSaveConfig}
        onDiscard={handleDiscard}
      />

      <PageHeader
        size="sm"
        title="Tiny ERP"
        description="Gerencie envios de bonificação e pedidos no Tiny."
        backHref="/settings/integrations"
      />

      {/* Connection Card */}
      <SectionCard
        title="Conexão"
        description="Conecte sua conta do Tiny ERP para criar pedidos de bonificação automaticamente."
      >
        {status?.enabled ? (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                Tiny ERP está conectado. Pedidos de seeding serão criados automaticamente.
              </AlertDescription>
            </Alert>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <ChonkyButton
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => testConnection.mutateAsync()}
                loading={testConnection.isPending}
              >
                <Zap className="h-3.5 w-3.5" />
                Testar Conexão
              </ChonkyButton>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full sm:w-auto text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Desconectar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Desconectar Tiny ERP?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Os pedidos de bonificação não serão mais criados automaticamente no Tiny. Você
                      pode reconectar a qualquer momento.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        removeTiny.mutateAsync()
                        setSynced(false)
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Desconectar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Token da API</Label>
              <div className="relative">
                <Input
                  type={showApiToken ? 'text' : 'password'}
                  placeholder="Token gerado no Tiny ERP"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowApiToken(!showApiToken)}
                >
                  {showApiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Encontre em Configurações {'>'} Integrações {'>'} Tokens e credenciais no Tiny.
              </p>
            </div>

            <ChonkyButton
              onClick={handleConnect}
              disabled={!apiToken.trim()}
              loading={saveTinyConfig.isPending}
            >
              Conectar
            </ChonkyButton>
          </div>
        )}
      </SectionCard>

      {/* Webhooks Card (only when connected) */}
      {status?.enabled && status.merchantId && (
        <>
          <Separator />
          <WebhookUrlCard merchantId={status.merchantId} />
        </>
      )}

      {/* Config Card (only when connected) */}
      {status?.enabled && (
        <>
          <Separator />

          <SectionCard
            title="Configurações de Seeding"
            description="Defina os padrões para criação de pedidos de bonificação no Tiny."
          >
            <div className="space-y-0 divide-y divide-border">
              {/* ── Seção: Pedido ───────────────────────────────────── */}
              <FormSection title="Pedido">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Status Padrão</Label>
                    <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDER_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Status ao criar o pedido no Tiny.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select value={formaPagamento} onValueChange={setFormaPagamento}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Order tags chip input — full width */}
                <div className="space-y-2">
                  <Label>
                    Marcadores do Pedido{' '}
                    <span className="text-muted-foreground font-normal">(opcional)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      ref={orderTagInputRef}
                      value={orderTagInput}
                      onChange={(e) => setOrderTagInput(e.target.value)}
                      onKeyDown={handleOrderTagKeyDown}
                      placeholder="Ex: Bonificação"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleAddOrderTag}
                      disabled={!orderTagInput.trim()}
                      className="shrink-0"
                      aria-label="Adicionar marcador"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {orderTagsArray.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {orderTagsArray.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 rounded-md bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveOrderTag(tag)}
                            className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring p-0.5 transition-colors"
                            aria-label={`Remover marcador ${tag}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Pressione Enter ou clique em + para adicionar. Criados automaticamente no Tiny
                    se não existirem.
                  </p>
                </div>
              </FormSection>

              {/* ── Seção: Envio ─────────────────────────────────────── */}
              <FormSection title="Envio" columns={2}>
                <div className="space-y-2">
                  <Label>Forma de Envio</Label>
                  <Select value={shippingMethod} onValueChange={setShippingMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {envioOptions.map((m) => (
                        <SelectItem key={m.code} value={m.code}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    Forma de Frete{' '}
                    <span className="text-muted-foreground font-normal">(opcional)</span>
                  </Label>
                  <Input
                    value={formaFrete}
                    onChange={(e) => setFormaFrete(e.target.value)}
                    placeholder="Ex: PAC, SEDEX, Mini Envios"
                  />
                  <p className="text-xs text-muted-foreground">
                    Nome da forma de frete cadastrada no Tiny.
                  </p>
                </div>

                {/* Transportadora — só aparece quando forma de envio = T */}
                {shippingMethod === 'T' && (
                  <div className="space-y-2 sm:col-span-2 sm:max-w-sm">
                    <Label>Nome da Transportadora</Label>
                    <Input
                      value={carrierName}
                      onChange={(e) => setCarrierName(e.target.value)}
                      placeholder="Ex: Jadlog, Total Express..."
                    />
                  </div>
                )}
              </FormSection>

              {/* ── Seção: Precificação ──────────────────────────────── */}
              <FormSection title="Precificação" columns={2}>
                <div className="space-y-2">
                  <Label>Estratégia de Preço dos Itens</Label>
                  <Select
                    value={itemPriceStrategy}
                    onValueChange={(v) => setItemPriceStrategy(v as typeof itemPriceStrategy)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICE_STRATEGIES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Define o valor unitário dos itens no pedido do Tiny.
                  </p>
                </div>

                {/* Percentual — visível apenas quando estratégia = percentage */}
                {itemPriceStrategy === 'percentage' && (
                  <div className="space-y-2">
                    <Label>Percentual do Preço de Venda</Label>
                    <div className="relative max-w-30">
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={itemPricePercent}
                        onChange={(e) => setItemPricePercent(e.target.value)}
                        placeholder="30"
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ex: 30% de R$ 100 = R$ 30 no pedido do Tiny.
                    </p>
                  </div>
                )}
              </FormSection>

              {/* ── Seção: Financeiro ────────────────────────────────── */}
              <FormSection title="Financeiro">
                <div className="space-y-2">
                  <Label>
                    Categorias Financeiras{' '}
                    <span className="text-muted-foreground font-normal">(opcional)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      ref={financeCategoryInputRef}
                      value={financeCategoryInput}
                      onChange={(e) => setFinanceCategoryInput(e.target.value)}
                      onKeyDown={handleFinanceCategoryKeyDown}
                      placeholder="Ex: Bonificação"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleAddFinanceCategory}
                      disabled={!financeCategoryInput.trim()}
                      className="shrink-0"
                      aria-label="Adicionar categoria"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {financeCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {financeCategories.map((cat) => {
                        const isDefault = cat === financeCategory
                        return (
                          <span
                            key={cat}
                            className={[
                              'inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-xs font-medium transition-colors',
                              isDefault
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary text-secondary-foreground hover:bg-secondary/70 cursor-pointer',
                            ].join(' ')}
                            onClick={() => !isDefault && setFinanceCategory(cat)}
                            role={!isDefault ? 'button' : undefined}
                            title={isDefault ? 'Categoria padrão' : 'Definir como padrão'}
                          >
                            {isDefault && <Star className="h-3 w-3 fill-current shrink-0" />}
                            {cat}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveFinanceCategory(cat)
                              }}
                              className="ml-0.5 rounded-sm hover:bg-white/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring p-0.5 transition-colors"
                              aria-label={`Remover categoria ${cat}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {financeCategories.length > 0
                      ? 'Clique em uma categoria para definir como padrão (marcada com ★).'
                      : 'Nome exato da categoria em Finanças > Categorias no Tiny.'}
                  </p>
                </div>
              </FormSection>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  )
}

function WebhookUrlCard({ merchantId }: { merchantId: string }) {
  const [copied, setCopied] = useState(false)
  const webhookUrl = `${API_BASE_URL}/api/webhooks/tiny/${merchantId}`

  const handleCopy = async () => {
    await navigator.clipboard.writeText(webhookUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <SectionCard
      title="Webhooks"
      description="Configure esta URL no painel do Tiny para receber atualizações de status e rastreio automaticamente."
    >
      <div className="space-y-2">
        <Label>URL do Webhook</Label>
        <div className="flex items-center gap-2">
          <Input value={webhookUrl} readOnly className="font-mono text-xs" />
          <ChonkyButton variant="secondary" size="icon" className="shrink-0" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </ChonkyButton>
        </div>
        <p className="text-xs text-muted-foreground">
          No Tiny: Configurações {'>'} E-commerce {'>'} Integrações {'>'} Notificações. Cole esta
          URL nos campos de "Situação do pedido" e "Código de rastreio".
        </p>
      </div>
    </SectionCard>
  )
}
